import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import type {IngestaJob} from '@/hooks/bateria/use-bateria'
import {faltaDelLote, paginasConDesenlace} from '@/lib/bateria'

// Un job en curso no tiene magnitud: no gasta el escalón lime de la rampa en decir «esperá».
const ESTADO_TONE: Record<IngestaJob['estado'], 'enCurso' | 'green' | 'amber'> = {
  enumerando: 'enCurso',
  procesando: 'enCurso',
  completo: 'green',
  completo_con_pendientes: 'amber',
  descartado: 'amber',
}

function porProcesarDe(job: IngestaJob): number {
  return Math.max(
    0,
    job.paginas_total - job.paginas_hechas - job.paginas_fallidas - job.paginas_en_cuarentena - job.paginas_en_blanco
  )
}

function esTerminal(job: IngestaJob): boolean {
  return job.estado === 'completo' || job.estado === 'completo_con_pendientes' || job.estado === 'descartado'
}

/** Una frase bajo el Tag («listo, con N por revisar») en vez de dos palabras que se contradicen. */
function estadoResumen(job: IngestaJob, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (job.estado === 'completo') return t('admin.bateria.ingesta.resumen.completo')
  if (job.estado === 'descartado') {
    return t('admin.bateria.ingesta.resumen.descartado', {count: job.paginas_asentadas})
  }
  if (job.estado === 'completo_con_pendientes') {
    const pendientes = job.paginas_fallidas + job.paginas_en_cuarentena
    return t('admin.bateria.ingesta.resumen.completoConPendientes', {count: pendientes})
  }
  return t('admin.bateria.ingesta.resumen.enCurso')
}

/** La tasa sale del propio lote; sin historia suficiente dice «calculando…», no una cifra que se desploma. */
function progresoEnTiempo(job: IngestaJob, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const falta = faltaDelLote(job, Date.now())
  const cuenta = {done: paginasConDesenlace(job), total: job.paginas_total}
  if (falta === null) return t('admin.bateria.ingesta.etaCalculando', cuenta)
  const minutos = Math.round(falta / 60_000)
  if (minutos < 1) return t('admin.bateria.ingesta.etaCasi', cuenta)
  return t('admin.bateria.ingesta.eta', {...cuenta, count: minutos})
}

function barraDetalle(
  job: IngestaJob,
  indeterminate: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  // Terminado, cuenta procesadas: «631 de 650» junto a una barra llena sugiere páginas perdidas.
  const parts = [
    indeterminate
      ? t('admin.bateria.ingesta.enumerando')
      : esTerminal(job)
        ? t('admin.bateria.ingesta.countProcesadas', {done: paginasConDesenlace(job), total: job.paginas_total})
        : t('admin.bateria.ingesta.count', {
            done: job.paginas_hechas + job.paginas_fallidas,
            total: job.paginas_total,
          }),
  ]
  if (job.paginas_fallidas > 0) parts.push(t('admin.bateria.ingesta.fallidas', {count: job.paginas_fallidas}))
  if (job.paginas_en_cuarentena > 0)
    parts.push(t('admin.bateria.ingesta.cuarentena', {count: job.paginas_en_cuarentena}))
  if (job.paginas_en_blanco > 0) parts.push(t('admin.bateria.ingesta.enBlanco', {count: job.paginas_en_blanco}))
  return parts.join(' · ')
}

export function IngestaBar({job}: {job: IngestaJob}) {
  const {t} = useTranslation()
  const running = job.estado === 'enumerando' || job.estado === 'procesando'
  const terminal = esTerminal(job)
  // Sin total conocido mientras corre, una fracción pintaría un falso 100%: barra indeterminada.
  const indeterminate = running && job.paginas_total === 0
  // Terminado, la barra cuenta desenlaces (ubicado, sin ubicar, fallido) y llega al 100% si ninguna
  // página quedó colgada.
  const avance = terminal ? paginasConDesenlace(job) : job.paginas_hechas + job.paginas_fallidas
  const pct = job.paginas_total > 0 ? Math.round((avance / job.paginas_total) * 100) : 100
  const anchoDe = (paginas: number) => (job.paginas_total > 0 ? (paginas / job.paginas_total) * 100 : 0)
  const detalle = barraDetalle(job, indeterminate, t)
  const porProcesar = porProcesarDe(job)

  return (
    <div className="surface rounded-lg px-4 py-3" data-testid={`ingesta-${job.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tag tone={ESTADO_TONE[job.estado]} data-testid={`ingesta-estado-${job.id}`}>
          {t(`admin.bateria.ingesta.estado.${job.estado}`)}
        </Tag>
        <span className="font-mono text-xs text-muted-foreground" data-testid={`ingesta-cuenta-${job.id}`}>
          {detalle}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground" data-testid={`ingesta-resumen-${job.id}`}>
        {estadoResumen(job, t)}
      </p>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-secondary outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              role="progressbar"
              aria-label={detalle}
              aria-valuenow={indeterminate ? undefined : pct}
              aria-valuemin={0}
              aria-valuemax={100}
              data-testid={`ingesta-barra-${job.id}`}
              data-indeterminate={indeterminate || undefined}>
              {indeterminate ? (
                <span className="block h-full w-1/3 animate-pulse rounded-full bg-primary" />
              ) : terminal ? (
                <span className="flex h-full w-full" data-testid={`ingesta-tramos-${job.id}`}>
                  <span
                    className="h-full bg-primary transition-all"
                    style={{width: `${anchoDe(job.paginas_hechas)}%`}}
                  />
                  <span
                    className="swatch-amber h-full transition-all"
                    data-testid={`ingesta-tramo-sin-ubicar-${job.id}`}
                    style={{width: `${anchoDe(job.paginas_en_cuarentena)}%`}}
                  />
                  <span
                    className="swatch-red h-full transition-all"
                    style={{width: `${anchoDe(job.paginas_fallidas)}%`}}
                  />
                  <span
                    className="h-full bg-muted-foreground/40 transition-all"
                    data-testid={`ingesta-tramo-en-blanco-${job.id}`}
                    style={{width: `${anchoDe(job.paginas_en_blanco)}%`}}
                  />
                </span>
              ) : (
                <span className="block h-full rounded-full bg-primary transition-all" style={{width: `${pct}%`}} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent data-testid={`ingesta-barra-tooltip-${job.id}`}>{detalle}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {running && !indeterminate && (
        <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`ingesta-eta-${job.id}`}>
          {progresoEnTiempo(job, t)}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
        {porProcesar > 0 && (
          <span data-testid={`ingesta-por-procesar-${job.id}`} className="tone-amber rounded-sm px-1">
            {t('admin.bateria.ingesta.cifra.porProcesar', {count: porProcesar})}
          </span>
        )}
        {job.paginas_fallidas > 0 && (
          <span data-testid={`ingesta-fallidas-${job.id}`} className="tone-red rounded-sm px-1">
            {t('admin.bateria.ingesta.fallidas', {count: job.paginas_fallidas})}
          </span>
        )}
        {job.paginas_en_cuarentena > 0 && (
          <span data-testid={`ingesta-cuarentena-${job.id}`} className="tone-amber rounded-sm px-1">
            {t('admin.bateria.ingesta.cuarentena', {count: job.paginas_en_cuarentena})}
          </span>
        )}
        {job.paginas_en_blanco > 0 && (
          <span data-testid={`ingesta-en-blanco-${job.id}`} className="rounded-sm px-1">
            {t('admin.bateria.ingesta.enBlanco', {count: job.paginas_en_blanco})}
          </span>
        )}
        {job.error && (
          <span data-testid={`ingesta-error-${job.id}`} className="tone-red rounded-sm px-1">
            {t('admin.bateria.ingesta.error')}
          </span>
        )}
      </div>
    </div>
  )
}
